/**
 * A beat's clock: how often it runs, and when it is next due.
 *
 * MOVED OUT OF `NewsBeats.tsx`, where `cadenceLabel` lived beside the list that
 * used it. The Beats home needs the same words — a cadence described one way on
 * the list and another way on the home is the sort of thing nobody notices until
 * a customer asks which is right — and it needs the DUE calculation the list
 * never had.
 *
 * Pure and dependency-free so CRA's jest can test it: react-router-dom v7 does
 * not resolve under this jest config, so anything worth asserting on has to live
 * apart from the components that import it. Same reason `deliverTabs.ts` and
 * `trendFreshness.ts` exist.
 */

const CADENCE_LABEL: Record<number, string> = {
  60: 'Hourly',
  360: 'Every 6h',
  720: 'Every 12h',
  1440: 'Daily',
  4320: 'Every 3 days',
  10080: 'Weekly',
};

export function cadenceLabel(minutes: number): string {
  if (CADENCE_LABEL[minutes]) return CADENCE_LABEL[minutes];
  if (minutes % 1440 === 0) return `Every ${minutes / 1440} days`;
  if (minutes % 60 === 0) return `Every ${minutes / 60}h`;
  return `Every ${minutes}m`;
}

/**
 * When this beat should next run, or null if that cannot be said.
 *
 * NULL FOR A BEAT THAT HAS NEVER RUN, deliberately. `last_run_at` is the only
 * anchor there is, so without it the honest answer is "unknown" — and the
 * tempting fallback, treating a never-run beat as due now, would file every
 * freshly-created beat under "overdue" on the screen you land on right after
 * creating one.
 */
export function nextRunAt(
  lastRunAt: string | null | undefined,
  intervalMinutes: number,
): Date | null {
  if (!lastRunAt) return null;
  const last = new Date(lastRunAt);
  if (Number.isNaN(last.getTime())) return null;
  return new Date(last.getTime() + intervalMinutes * 60_000);
}

/**
 * Has this beat missed its slot by a wide enough margin to be worth saying?
 *
 * ONE FULL INTERVAL LATE, not one minute. A scheduler that fires on a cadence is
 * always a little behind — the run takes time, the worker was busy — and a home
 * screen that shouts "overdue" every time a daily beat runs at 09:01 instead of
 * 09:00 is one people stop reading. A beat that has missed an entire cycle has
 * actually stopped.
 */
export function isOverdue(
  lastRunAt: string | null | undefined,
  intervalMinutes: number,
  now: Date = new Date(),
): boolean {
  const due = nextRunAt(lastRunAt, intervalMinutes);
  if (!due) return false;
  return now.getTime() > due.getTime() + intervalMinutes * 60_000;
}
